import moment from 'moment';
import Config from '../config/Config';
import Utils from './Utils';
import { parseUrlContext } from '../routing/contextUrl';
import { build_preview_booking_summary_context } from './bookingSummaryPreview';
import { build_preview_custom_text, build_preview_gift_parts, build_preview_order_parts, build_preview_refund_parts } from './emailOrderPartsPreview';
import { build_distributor_report_summary_plain } from './distributorReportPreview';
import { resolve_eshop_url_override } from './eshopUrlOverride';

/** Content column width in engine/mailer/EmailDocument.js (centered-table). */
export const EMAIL_LAYOUT_PREVIEW_CONTENT_MAX_WIDTH_PX = 550;

const DISTRIBUTOR_REPORT_SUMMARY_SHORTCODE = '[distributor_report.summary]';

function is_plain_object(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Deep-merge plain objects (settings / overrides).
 *
 * @param {Object} target
 * @param {Object} source
 * @returns {Object}
 */
function deep_merge_settings(target, source) {
    if (!source)
        return target || {};
    const out = { ...(target || {}) };
    Object.keys(source).forEach(key => {
        if (is_plain_object(source[key]) && is_plain_object(out[key]))
            out[key] = deep_merge_settings(out[key], source[key]);
        else
            out[key] = source[key];
    });
    return out;
}

const SHORTCODE_PATTERN = /\[(?:(?<resource>[^.\]]+)(?:\.))?((?<path>[^\]]+))\]/gm;

const PREVIEW_CONTACT_BY_LANG = {
    fr: { title: 'M.', firstname: 'Jean', lastname: 'Dupont', email: 'exemple@example.com' },
    en: { title: 'Mr', firstname: 'John', lastname: 'Smith', email: 'example@example.com' },
    de: { title: 'Herr', firstname: 'Hans', lastname: 'Müller', email: 'beispiel@example.com' },
};

export const PREVIEW_SEAT = 'Parterre:5:12';
export const PREVIEW_SCREENING_ID = 'preview-screening';
export const PREVIEW_BOOKING_ID = 'preview-booking';

const PREVIEW_SCREENING_TITLE = {
    fr: 'Le voyage extraordinaire',
    en: 'The extraordinary journey',
    de: 'Die außergewöhnliche Reise',
    it: 'Il viaggio straordinario',
};

const PREVIEW_FILM_OPAQUE = {
    l_min_age: '12',
    s_min_age: '10',
    a_min_age: '8',
};

const PREVIEW_PLACE_BY_LANG = {
    fr: {
        _id: 'preview-place',
        cinema: 'Cinéma Example',
        number: '1',
        name: 'Salle 1',
        address: 'Rue Example 1',
        zip: '1000',
        city: 'Lausanne',
        country: 'Suisse',
    },
    en: {
        _id: 'preview-place',
        cinema: 'Example Cinema',
        number: '1',
        name: 'Screen 1',
        address: 'Example Street 1',
        zip: '1200',
        city: 'Geneva',
        country: 'Switzerland',
    },
    de: {
        _id: 'preview-place',
        cinema: 'Beispielkino',
        number: '1',
        name: 'Saal 1',
        address: 'Beispielstrasse 1',
        zip: '8001',
        city: 'Zürich',
        country: 'Schweiz',
    },
};

const PREVIEW_DEFAULT_PRICING = {
    name: {
        fr: 'Tarif plein',
        en: 'Full price',
        de: 'Vollpreis',
        it: 'Tariffa intera',
    },
    description: {
        fr: 'Place assise',
        en: 'Seated admission',
        de: 'Sitzplatz',
        it: 'Posto a sedere',
    },
    price: { CHF: 12 },
    VAT: 8.1,
};

const PREVIEW_DEFAULT_TICKETTYPE = {
    _id: 'preview-tickettype',
    name: {
        fr: 'Preview',
        en: 'Preview',
        de: 'Preview',
        it: 'Preview',
    },
    description: {
        fr: 'Description du type de billet',
        en: 'Ticket type description',
        de: 'Beschreibung des Tickettyps',
        it: 'Descrizione del tipo di biglietto',
    },
};

function preview_lang_key(lang) {
    return (lang || 'fr').substring(0, 2);
}

function preview_eshop_base(setting) {
    const raw = setting?.eshop?.uri || 'https://example.ticketack.com';
    return Utils.normalizeEshopUri(raw) || 'https://example.ticketack.com';
}

const PREVIEW_PLANNING_HEADERS = {
    fr: ['Début', 'Fin', 'Secteur', 'Responsables'],
    en: ['Start', 'End', 'Sector', 'Contacts'],
    de: ['Beginn', 'Ende', 'Bereich', 'Verantwortliche'],
    it: ['Inizio', 'Fine', 'Settore', 'Responsabili'],
};

/**
 * Sample shift table shown where [application.user.planning] is inserted.
 *
 * @param {string} [lang]
 * @returns {string}
 */
export function preview_volunteer_planning_table(lang) {
    const key = preview_lang_key(lang);
    const headers = PREVIEW_PLANNING_HEADERS[key] || PREVIEW_PLANNING_HEADERS.fr;
    const rows = [
        ['ven. 30 oct. 2026 14:00', 'ven. 30 oct. 2026 18:00', 'Accueil<br><small>Entrée principale, Place du Festival 1</small>', 'Alex Martin (079 000 00 00)'],
        ['sam. 31 oct. 2026 10:00', 'sam. 31 oct. 2026 14:00', 'Salle<br><small>Entrée principale, Place du Festival 1</small>', 'Sam Rossi'],
    ];
    const header = headers.map(label => `<th align="center">${label}</th>`).join('');
    const body = rows.map((cells, index) => {
        const color = index % 2 === 0 ? '#FAFAFA' : '#F0F0F0';
        const tds = cells.map(cell => `<td>${cell}</td>`).join('');
        return `<tr bgcolor="${color}">${tds}</tr>`;
    }).join('');

    return `<table width="100%" border="1" cellpadding="5"><tr bgcolor="#F0F0F0">${header}</tr>${body}</table>`;
}

/**
 * Logged-in planning page for the layout preview.
 * Uses the editor origin and /i/{slug} when the browser URL has one.
 *
 * @returns {string}
 */
export function preview_planning_page_url() {
    const in_browser = typeof window !== 'undefined' && window.location && window.location.origin;
    const origin = in_browser
        ? String(window.location.origin).replace(/\/$/, '')
        : 'https://kronos.example';
    let slug = 'preview';
    if (in_browser) {
        const parsed = parseUrlContext();
        if (parsed.slug)
            slug = parsed.slug;
    }

    return `${origin}/i/${encodeURIComponent(slug)}/tasks`;
}

/**
 * Sample ICS subscription URL. Uses a preview key, never a real API key.
 *
 * @returns {string}
 */
export function preview_planning_ics_url() {
    const base = String(Config.api_end_point || 'https://api.example').replace(/\/$/, '');
    const params = new URLSearchParams({
        'x-api-key': 'preview',
        user_id: 'preview-user',
        tkt_instance: 'preview-instance',
    });

    return `${base}/task/ics?${params.toString()}`;
}

/**
 * Mirror Engine TicketLinksShortcode.engineBaseUri (eshop host → *-engine.*).
 *
 * @param {string} eshop_base
 * @returns {string|null}
 */
export function preview_engine_base_from_eshop(eshop_base) {
    const eshop = String(eshop_base || '').replace(/-eshop/g, '');
    const match = [...eshop.matchAll(/(https?):\/\/([^.]+)(-[^.]+)?\.([^/]+)/gm)];
    if (!match?.length)
        return null;
    const [, scheme, prefix, , domain] = match[0];
    return `${scheme}://${prefix}-engine.${domain}`;
}

/**
 * Preview [ticket.download_link.*] — same shape as TicketLinksShortcode download.
 *
 * @param {string} eshop_base
 * @param {string} ticket_id
 * @param {string} lang
 * @param {{ printer?: string, version?: number }} [opts]
 * @returns {string}
 */
export function preview_ticket_download_link(eshop_base, ticket_id, lang, opts = {}) {
    const engine_base = preview_engine_base_from_eshop(eshop_base);
    if (!engine_base || !ticket_id)
        return '';
    const printer = opts.printer ?? 'preview';
    const version = opts.version ?? 1;
    const locale = String(lang || 'fr').substring(0, 2);
    return `${engine_base}/api/third/tickets/pdf/ticket-${ticket_id}.pdf`
        + `?lang=${locale}&printer=${encodeURIComponent(printer)}`
        + `&ticket_id_list=${ticket_id}&layout=paper&v=${version}`;
}

/**
 * @param {string} lang
 * @returns {{ firstname: string, lastname: string, email: string }}
 */
export function get_preview_sample_contact(lang) {
    const key = (lang || 'fr').substring(0, 2);
    return PREVIEW_CONTACT_BY_LANG[key] || PREVIEW_CONTACT_BY_LANG.fr;
}

/**
 * Resolve eshop.* shortcode paths. For url_overrides.*, mirrors Eshop AppRouter
 * (see eshopUrlOverride.js / engine/modules/eshop_url_override.js).
 *
 * @param {Object} eshop
 * @param {string} path_str
 * @param {{ default_lang?: string }} [options]
 * @returns {string|number|boolean|null}
 */
function resolve_eshop_path(eshop, path_str, options = {}) {
    if (!eshop)
        return null;

    if (path_str.startsWith('url_overrides.'))
        return resolve_eshop_url_override(eshop, path_str, options);

    const value = Utils.getByPath(eshop, path_str);
    if (typeof value === 'string' && value.length > 0)
        return value;
    if (typeof value === 'number' || typeof value === 'boolean')
        return value;

    return null;
}
const MOMENT_DATE_FORMAT_ALIASES = {
    DMMMM: 'D MMMM',
    DMMM: 'D MMM',
    MMMMD: 'MMMM D',
    MMMD: 'MMM D',
    'D.MMMM': 'D. MMMM',
    'D.MMM': 'D. MMM',
};
const MOMENT_DATE_FORMATS = [
    'DDDo', 'dddd', 'ddd', 'dd', 'do', 'd', 'YYYY', 'HH', 'mm',
    'DMMMM', 'DMMM', 'MMMMD', 'MMMD', 'D.MMMM', 'D.MMM',
    'LLLL', 'llll', 'LLL', 'lll', 'LTS', 'LT', 'LL', 'll', 'L', 'I',
];
/** Locales allowed as `[…_LL.en]` overrides (same set as translatable fields). */
const SHORTCODE_DATE_LOCALES = ['fr', 'en', 'de', 'it'];

/**
 * Parse a shortcode property path for an optional Moment format suffix,
 * optional day offset (`+N` / `-N`) before that format, and optional
 * locale override (`.fr` / `.en` / …) after the format — mirrors Engine
 * `parse_shortcode_date_path`.
 *
 * @param {string} path
 * @returns {{
 *   path: string,
 *   dateFormat: string|null,
 *   dayOffset: number|null,
 *   locale: string|null,
 * }}
 */
function parse_shortcode_date_path(path) {
    let remaining = String(path || '');
    let dateFormat = null;
    let dayOffset = null;
    let locale = null;

    for (let i = 0; i < MOMENT_DATE_FORMATS.length; i++) {
        const format = MOMENT_DATE_FORMATS[i];
        let matched = false;
        for (let j = 0; j < SHORTCODE_DATE_LOCALES.length; j++) {
            const loc = SHORTCODE_DATE_LOCALES[j];
            const with_locale = `_${format}.${loc}`;
            if (remaining.length >= with_locale.length
                && remaining.slice(-with_locale.length).toLowerCase() === with_locale.toLowerCase()) {
                remaining = remaining.slice(0, -with_locale.length);
                dateFormat = MOMENT_DATE_FORMAT_ALIASES[format] || format;
                locale = loc;
                matched = true;
                break;
            }
        }
        if (matched)
            break;

        const suffix = `_${format}`;
        if (remaining.endsWith(suffix)) {
            remaining = remaining.slice(0, -suffix.length);
            dateFormat = MOMENT_DATE_FORMAT_ALIASES[format] || format;
            break;
        }
    }

    if (dateFormat) {
        const offset_match = remaining.match(/^(.+)([+-]\d+)$/);
        if (offset_match) {
            remaining = offset_match[1];
            dayOffset = parseInt(offset_match[2], 10);
        }
    }

    return { path: remaining, dateFormat, dayOffset, locale };
}

/**
 * Optional locale on non-date paths like `ordinal.en` (multilingual-style).
 * Callers must only apply when path is `ordinal` after strip.
 *
 * @param {string} path
 * @returns {{ path: string, locale: string|null }}
 */
function parse_shortcode_locale_suffix(path) {
    const raw = String(path || '');
    const match = raw.match(/^(.+)\.(fr|en|de|it)$/i);
    if (!match)
        return { path: raw, locale: null };
    return { path: match[1], locale: match[2].toLowerCase() };
}

const PREVIEW_EDITION = {
    _id: '2026',
    number: 62,
    start_date: '2026-07-01T00:00:00.000Z',
    end_date: '2026-08-31T23:59:59.999Z',
    event_start_date: '2026-08-03T00:00:00.000Z',
    event_end_date: '2026-08-09T23:59:59.999Z',
};

export { PREVIEW_EDITION };

/**
 * Resolve the edition document used for layout preview shortcodes.
 * Prefer an explicit edition, then layout.edition / setting.edition / selected
 * edition looked up in `editions`, falling back to PREVIEW_EDITION samples.
 *
 * @param {{
 *   edition?: Object|null,
 *   setting?: Object|null,
 *   layout?: Object|null,
 *   editions?: Array|null,
 *   selected_edition_id?: string|null,
 * }} [params]
 * @returns {Object}
 */
export function resolve_preview_edition({
    edition,
    setting,
    layout,
    editions,
    selected_edition_id,
} = {}) {
    if (edition && typeof edition === 'object' && (edition.number != null || edition._id)) {
        return {
            ...PREVIEW_EDITION,
            ...edition,
            _id: edition._id != null ? String(edition._id) : PREVIEW_EDITION._id,
        };
    }

    const wanted = String(
        layout?.edition
        || setting?.edition
        || selected_edition_id
        || ''
    ).trim();

    const list = Array.isArray(editions) ? editions : [];
    const found = wanted
        ? list.find((e) => String(e?._id || e?.id || '') === wanted)
        : null;

    if (found) {
        return {
            ...PREVIEW_EDITION,
            ...found,
            _id: String(found._id || found.id || wanted),
            number: found.number != null ? Number(found.number) : PREVIEW_EDITION.number,
            start_date: found.start_date || PREVIEW_EDITION.start_date,
            end_date: found.end_date || PREVIEW_EDITION.end_date,
            event_start_date: found.event_start_date || PREVIEW_EDITION.event_start_date,
            event_end_date: found.event_end_date || PREVIEW_EDITION.event_end_date,
        };
    }

    if (wanted) {
        return {
            ...PREVIEW_EDITION,
            _id: wanted,
        };
    }

    return { ...PREVIEW_EDITION };
}

/**
 * @param {*} value
 * @returns {boolean}
 */
function is_shortcode_replaceable(value) {
    if (value === null || value === undefined)
        return false;
    const value_type = typeof value;
    // Match engine/modules/translatable_shortcode.js: empty string clears the token.
    if (value_type === 'string')
        return true;
    if (value_type === 'number' || value_type === 'boolean')
        return true;
    return false;
}

function default_lang_from_context(context) {
    return String(context?.l10n?.locales?.default || 'fr').substring(0, 2).toLowerCase();
}

function localized_or_fallback_from_context(obj, lang, context) {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj))
        return '';

    if (obj[lang])
        return obj[lang];

    const default_lang = default_lang_from_context(context);
    if (default_lang && default_lang !== lang && obj[default_lang])
        return obj[default_lang];

    for (const key of Object.keys(obj)) {
        if (obj[key])
            return `${obj[key]}`;
    }

    return '';
}

/**
 * Derive seat_zone / seat_row / seat_number from `bookings.N.seat` ("zone:row:number").
 * Matches engine CustomTextWidget and mailer ContextPropertyShortcode.
 *
 * @param {Object} source
 * @param {string} path_str
 * @returns {string|undefined}
 */
function resolve_seat_path_value(source, path_str) {
    let index = null;
    let seat_path = path_str;
    if (path_str.match(/seat_zone$/)) {
        seat_path = path_str.replace('seat_zone', 'seat');
        index = 0;
    } else if (path_str.match(/seat_row$/)) {
        seat_path = path_str.replace('seat_row', 'seat');
        index = 1;
    } else if (path_str.match(/seat_number$/)) {
        seat_path = path_str.replace('seat_number', 'seat');
        index = 2;
    } else {
        return undefined;
    }

    return (Utils.getByPath(source, seat_path)?.split(':') || [])[index];
}

function resolve_shortcode_path_value(source, path_str, context) {
    let value = Utils.getByPath(source, path_str);
    if (is_shortcode_replaceable(value))
        return value;

    const seat_value = resolve_seat_path_value(source, path_str);
    if (is_shortcode_replaceable(seat_value))
        return seat_value;

    const lang_match = path_str.match(/^(.+)\.([a-z]{2})$/i);
    if (!lang_match)
        return value;

    const [, parent_path, lang] = lang_match;
    const parent = Utils.getByPath(source, parent_path);
    if (!parent || typeof parent !== 'object' || Array.isArray(parent))
        return value;

    const fallback = localized_or_fallback_from_context(parent, lang, context);
    return is_shortcode_replaceable(fallback) ? fallback : value;
}

/**
 * Replace shortcodes; keep the original token when the value cannot be resolved.
 *
 * @param {string} text
 * @param {Object} context
 * @returns {string}
 */
export function replaceShortcodesPreservingUnresolved(text, context) {
    if (!text)
        return text ?? '';

    const match = [...text.matchAll(SHORTCODE_PATTERN)];
    if (!match.length)
        return text;

    match.forEach(m => {
        const full = m[0];
        const { resource } = m.groups;
        let path_str = m.groups.path;
        const source = (resource ? context[resource] : context) || {};

        const parsed = parse_shortcode_date_path(path_str);
        path_str = parsed.path;
        const date_format = parsed.dateFormat;
        const day_offset = parsed.dayOffset;
        let locale = parsed.locale;

        if (!date_format && !locale) {
            const locale_parsed = parse_shortcode_locale_suffix(path_str);
            // Only strip `.en` for ordinal (not customer.name.fr — that stays on path).
            if (locale_parsed.locale && locale_parsed.path === 'ordinal') {
                path_str = locale_parsed.path;
                locale = locale_parsed.locale;
            }
        }

        const format_lang = locale || context.lang;

        let value;
        if (resource === 'edition' && path_str === 'ordinal') {
            value = Utils.formatEditionOrdinal(source.number, format_lang);
        } else {
            value = resolve_shortcode_path_value(source, path_str, context);
        }

        // Common legacy shortcodes: any `*_count` becomes a stable sample integer.
        if (!is_shortcode_replaceable(value) && /(^|\.)([a-z0-9_]+_count)$/i.test(path_str))
            value = 42;

        if (resource === 'eshop') {
            const eshop_value = resolve_eshop_path(source, path_str, {
                default_lang: default_lang_from_context(context),
            });
            if (path_str.startsWith('url_overrides.') || (
                eshop_value != null && (!is_shortcode_replaceable(value) || value === '')
            ))
                value = eshop_value;
        }

        if (date_format) {
            if (value === null || value === undefined || value === '') {
                // Keep unresolved only when there was no date value to format.
                return;
            }
            value = moment(value);
            if (day_offset != null)
                value = value?.add(day_offset, 'days');
            if (format_lang)
                value = value?.locale(format_lang);
            value = value?.format(date_format);
        }

        if (!is_shortcode_replaceable(value))
            return;

        text = text.replace(full, String(value));
    });

    return text;
}

/**
 * @param {Object} setting
 * @returns {Object}
 */
export function setting_to_plain(setting) {
    if (!setting)
        return {};
    if (typeof setting === 'object' && !Array.isArray(setting))
        return { ...setting };
    return {};
}

/**
 * Merge global/edition settings with a salepoint override block.
 *
 * @param {Object} base_setting
 * @param {string|undefined} salepoint_id
 * @param {Array} salepoints
 * @returns {Object}
 */
export function build_effective_preview_settings(base_setting, salepoint_id, salepoints = []) {
    const base = setting_to_plain(base_setting);
    if (!salepoint_id)
        return base;

    const salepoint = (salepoints || []).find(sp => String(sp._id) === String(salepoint_id));
    if (!salepoint?.settings || !Object.keys(salepoint.settings).length)
        return base;

    return deep_merge_settings(base, salepoint.settings);
}

function build_preview_activated_pricing(pricing) {
    if (pricing && (pricing.name || pricing.description || pricing.price)) {
        return {
            key: pricing.key || 'preview',
            name: pricing.name || PREVIEW_DEFAULT_PRICING.name,
            description: pricing.description || PREVIEW_DEFAULT_PRICING.description,
            price: pricing.price || PREVIEW_DEFAULT_PRICING.price,
            VAT: pricing.VAT ?? PREVIEW_DEFAULT_PRICING.VAT,
        };
    }
    return { ...PREVIEW_DEFAULT_PRICING, key: 'preview' };
}

function build_preview_place(place, lang) {
    if (place && (place.name || place.cinema || place.address || place._id))
        return place;

    const key = preview_lang_key(lang);
    return PREVIEW_PLACE_BY_LANG[key] || PREVIEW_PLACE_BY_LANG.fr;
}

function build_preview_screening(programmation, place, lang) {
    const fallback_place = build_preview_place(place, lang);
    if (programmation && (programmation.title || programmation._id || programmation.id || programmation.start_at)) {
        return {
            _id: programmation._id || programmation.id || PREVIEW_SCREENING_ID,
            title: programmation.title || PREVIEW_SCREENING_TITLE,
            start_at: programmation.start_at || '1970-12-01T20:00:00.000Z',
            stop_at: programmation.stop_at || programmation.start_at || '1970-12-01T22:30:00.000Z',
            films: [{
                _id: 'preview-film',
                title: programmation.title || PREVIEW_SCREENING_TITLE,
                opaque: { ...PREVIEW_FILM_OPAQUE },
            }],
            cinema_hall: {
                _id: fallback_place._id,
                cinema: fallback_place.cinema,
                number: fallback_place.number,
                name: fallback_place.name,
                address: fallback_place.address,
                zip: fallback_place.zip,
                city: fallback_place.city,
                country: fallback_place.country,
            },
        };
    }

    return {
        _id: PREVIEW_SCREENING_ID,
        title: PREVIEW_SCREENING_TITLE,
        start_at: '1970-12-01T20:00:00.000Z',
        stop_at: '1970-12-01T22:30:00.000Z',
        films: [{
            _id: 'preview-film',
            title: PREVIEW_SCREENING_TITLE,
            opaque: { ...PREVIEW_FILM_OPAQUE },
        }],
        cinema_hall: {
            _id: fallback_place._id,
            cinema: fallback_place.cinema,
            number: fallback_place.number,
            name: fallback_place.name,
            address: fallback_place.address,
            zip: fallback_place.zip,
            city: fallback_place.city,
            country: fallback_place.country,
        },
    };
}

/**
 * Ticket used for preview shortcodes (e.g. [ticket.type.name.fr], [ticket.bookings.0.seat_zone]).
 *
 * @param {Object} [tickettype]
 * @param {string} [lang]
 * @param {Object} [pricing]
 * @param {Object} [setting]
 * @returns {Object}
 */
export function build_preview_ticket(tickettype, lang, pricing, setting) {
    const sample = get_preview_sample_contact(lang);
    const type = tickettype || PREVIEW_DEFAULT_TICKETTYPE;
    const eshop_base = preview_eshop_base(setting);
    const ticket_id = '00000000-0000-4000-8000-000000000099';

    return {
        _id: ticket_id,
        type,
        vdr_auth_serial: 'PREVIEW',
        vdr_auth_key: '0000',
        activated_pricing: build_preview_activated_pricing(pricing),
        contact: { ...sample },
        bookings: [{
            _id: PREVIEW_BOOKING_ID,
            screening_id: PREVIEW_SCREENING_ID,
            seat: PREVIEW_SEAT,
        }],
        view_link: {
            fr: `${eshop_base}/ticket/view/uuid/${ticket_id}?lang=fr`,
            en: `${eshop_base}/ticket/view/uuid/${ticket_id}?lang=en`,
            de: `${eshop_base}/ticket/view/uuid/${ticket_id}?lang=de`,
            it: `${eshop_base}/ticket/view/uuid/${ticket_id}?lang=it`,
        },
        download_link: {
            fr: preview_ticket_download_link(eshop_base, ticket_id, 'fr'),
            en: preview_ticket_download_link(eshop_base, ticket_id, 'en'),
            de: preview_ticket_download_link(eshop_base, ticket_id, 'de'),
            it: preview_ticket_download_link(eshop_base, ticket_id, 'it'),
        },
    };
}

/**
 * Context for [resource.path] shortcodes in email / PDF layout preview.
 *
 * @param {{ setting?: Object, tickettype?: Object, pricing?: Object, programmation?: Object, place?: Object, lang?: string, order_parts_variant?: string, ticket_col_label?: string, include_booking_summary?: boolean }} params
 * @returns {Object}
 */
export function buildEmailLayoutPreviewContext({
    setting,
    tickettype,
    pricing,
    programmation,
    place,
    lang,
    order_parts_variant,
    ticket_col_label,
    include_booking_summary,
    edition,
    layout,
    editions,
    selected_edition_id,
}) {
    const effective = setting_to_plain(setting);
    const ticket = build_preview_ticket(tickettype, lang, pricing, effective);
    const sample = get_preview_sample_contact(lang);
    const preview_place = build_preview_place(place, lang);
    const screening = build_preview_screening(programmation, preview_place, lang);
    const order_parts = build_preview_order_parts({
        lang,
        setting: effective,
        tickettype: tickettype || ticket.type,
        contact: ticket.contact,
        variant: order_parts_variant,
        ticket_col_label,
        layout,
    });
    const refund_parts = build_preview_refund_parts({
        lang,
        setting: effective,
    });
    const gift_parts = build_preview_gift_parts({
        lang,
        setting: effective,
        contact: ticket.contact,
    });
    const preview_edition = resolve_preview_edition({
        edition,
        setting: effective,
        layout,
        editions,
        selected_edition_id,
    });

    const context = {
        ...effective,
        lang,
        firstname: sample.firstname,
        lastname: sample.lastname,
        zip: preview_place.zip || '1000',
        city: preview_place.city,
        address: preview_place.address,
        phone: '+41 21 555 12 34',
        cart: { id: 1234, status: 'COMPLETED' },
        order: {
            id: '12345',
            parts: order_parts,
            user_data: {
                title: sample.title,
                firstname: sample.firstname,
                lastname: sample.lastname,
                email: sample.email,
            },
        },
        refund: {
            cart_id: '12345',
            parts: refund_parts,
        },
        gift: {
            cart_id: '12345',
            parts: gift_parts,
        },
        custom: {
            text: build_preview_custom_text(),
        },
        ticket,
        tickettype: tickettype || ticket.type,
        screening,
        place: preview_place,
        edition: preview_edition,
        user: {
            id: 'preview-user',
            ...sample,
        },
        application: {
            id: 'preview-application',
            user: {
                id: 'preview-user',
                ...sample,
                planning: preview_volunteer_planning_table(lang),
                planning_url: preview_planning_page_url(),
                planning_ics: preview_planning_ics_url(),
            },
        },
        workflow: {
            resume_link: 'https://kronos.example/public/demo/workflow/resume?token=preview',
        },
    };

    if (include_booking_summary) {
        const booking_ctx = build_preview_booking_summary_context({
            lang,
            setting: effective,
            tickettype: tickettype || ticket.type,
            contact: ticket.contact,
        });
        Object.assign(context, booking_ctx);
    }

    return context;
}

/**
 * Alias used by PDF custom-text preview (same mock context as e-mail layouts).
 *
 * @param {Object} params
 * @returns {Object}
 */
export function buildLayoutPreviewShortcodeContext(params) {
    return buildEmailLayoutPreviewContext(params);
}

/**
 * Resolve shortcodes in email / PDF layout canvas preview text.
 * Always resolves (including edit mode): the WYSIWYG sidebar keeps raw tags.
 *
 * @param {string} text
 * @param {{ context?: Object, setting?: Object, tickettype?: Object, lang?: string, inEdition?: boolean }} options
 * @returns {string}
 */
export function resolveEmailLayoutPreviewText(text, { context, setting, tickettype, lang, inEdition: _inEdition }) {
    if (!text)
        return text ?? '';

    const resolved_context = context || buildEmailLayoutPreviewContext({ setting, tickettype, lang });
    return replaceShortcodesPreservingUnresolved(text, { ...resolved_context, lang });
}

/**
 * Resolve e-mail subject for layout preview (plain-text shortcodes before generic resolution).
 * Aligned with engine subject rendering (preventHtml on DistributorReportShortcode).
 *
 * @param {string} subject_raw
 * @param {Object} preview_context
 * @param {string} lang
 * @returns {string}
 */
export function resolve_email_layout_subject_preview(subject_raw, preview_context, lang) {
    let text = subject_raw || '';
    if (text.includes(DISTRIBUTOR_REPORT_SUMMARY_SHORTCODE)) {
        const plain = preview_context?.distributor_report?.summary_plain
            || build_distributor_report_summary_plain(preview_context?.distributor_report || {});
        text = text.split(DISTRIBUTOR_REPORT_SUMMARY_SHORTCODE).join(plain);
    }
    return resolveEmailLayoutPreviewText(text, {
        context: preview_context,
        lang,
        inEdition: false,
    });
}

/**
 * Build default From header (aligned with engine mailer defaults).
 *
 * @param {Object} setting
 * @param {string} lang
 * @returns {string}
 */
export function build_email_preview_from(setting, lang) {
    const name = Utils.localized_or_fallback(setting?.customer?.name, lang, '');
    const address = 'tickets@ticketack.com';
    return name ? `"${name}" <${address}>` : address;
}

function resolve_email_preview_header_value(value, { preview_context, lang }) {
    if (!value)
        return '';
    const raw = String(value);
    if (!raw.includes('['))
        return raw;
    return resolveEmailLayoutPreviewText(raw, {
        context: preview_context,
        lang,
        inEdition: false,
    });
}

/**
 * Resolve Reply-To header for e-mail layout preview (layout override or customer default).
 *
 * @param {Object} setting
 * @param {string} lang
 * @param {Object} [layout_options]
 * @param {Object} [preview_context]
 * @returns {string}
 */
export function build_email_preview_reply_to(setting, lang, layout_options, preview_context) {
    const layout_reply_to = layout_options?.replyTo;
    const default_reply = Utils.localized_or_fallback(setting?.customer?.email?.mail, lang, '');
    return resolve_email_preview_header_value(layout_reply_to || default_reply, { preview_context, lang });
}

/**
 * Resolve Bcc header for e-mail layout preview (layout override or customer default).
 *
 * @param {Object} setting
 * @param {Object} [layout_options]
 * @param {Object} [preview_context]
 * @param {string} [lang]
 * @returns {string}
 */
export function build_email_preview_bcc(setting, layout_options, preview_context, lang) {
    const layout_bcc = layout_options?.bcc;
    const default_bcc = setting?.customer?.email?.bcc || '';
    return resolve_email_preview_header_value(layout_bcc || default_bcc, { preview_context, lang });
}
